import React from 'react';
import {
  ProductCollectionSlice,
  ProductCollection,
  Breadcrumb as BreadcrumbType,
  ProductCollectionPath,
  AdvizedContentFragment,
} from '../../../../types';
import HeaderBlock from '../header-block';
import Products from '../products';
import Description from '../description';
import Offers from '../offers';
import Filter from '../filter';
import Breadcrumb from '../breadcrumb';
import Navigation from '../navigation';
import SecondaryNavigation from '../secondary-navigation';

type Props = {
  slice: ProductCollectionSlice;
  productCollection: ProductCollection;
};

const formatBreadcrumbs = (
  sliceItems: Array<BreadcrumbType>,
  categoryPaths: Array<ProductCollectionPath>
): Array<BreadcrumbType> => {
  return [
    ...categoryPaths
      .map(({ name, pathName }) => {
        return {
          name,
          pathName,
        };
      })
      .filter(c => !sliceItems.some(si => si.pathName === c.pathName)),
    ...sliceItems,
  ];
};

/**
 * Resolves a slice to the correct component by type.
 */
const Slice = ({ slice, productCollection }: Props) => {
  switch (slice.__typename) {
    case 'HeaderBlockSlice':
      return (
        <HeaderBlock
          id={slice.id}
          title={slice.title}
          description={slice.description}
          pagination={slice.pagination}
        />
      );
    case 'DescriptionSlice':
      return (
        <Description
          productCollection={productCollection}
          description={slice.description}
        />
      );
    case 'ProductsSlice': {
      return (
        <Products
          id={slice.id}
          pageInfo={slice.pageInfo}
          products={slice.products}
          productsCount={slice.productsCount}
          storeCount={slice.storeCount}
          legalText={slice.legalText}
          availableSorts={slice.availableSorts}
          currentSort={slice.currentSort}
          maxHitsAmount={slice.maxHitsAmount}
          sortDirection={slice.sortDirection}
          propertyColumns={slice.propertyColumns}
          advized={productCollection.advized as AdvizedContentFragment}
          productCollection={productCollection}
        />
      );
    }
    case 'OffersSlice':
      return (
        <Offers
          id={slice.id}
          pageInfo={slice.pageInfo}
          offers={slice.offers}
          productsCount={slice.productsCount}
          storeCount={slice.storeCount}
          legalText={slice.legalText}
          availableSorts={slice.availableSorts}
          currentSort={slice.currentSort}
          maxHitsAmount={slice.maxHitsAmount}
        />
      );
    case 'FilterSlice':
      return (
        <Filter
          id={slice.id}
          sections={slice.sections}
          filters={slice.filters}
          productsCount={slice.productsCount}
        />
      );
    case 'BreadcrumbsSlice':
      return (
        <Breadcrumb
          id={slice.id}
          items={formatBreadcrumbs(slice.items, productCollection.categoryPath)}
        />
      );
    case 'NavigationSlice':
      return (
        <Navigation
          id={slice.id}
          links={slice.links}
          property={slice.property}
          key={`${slice.id}_${
            Array.isArray(slice.links)
              ? slice.links.map(({ id }) => id).join(',')
              : ''
          }`}
        />
      );
    case 'SecondaryNavigationSlice':
      return (
        <SecondaryNavigation
          id={slice.id}
          productCollection={productCollection}
          groups={slice.groups}
          key={`${slice.id}_${
            Array.isArray(slice.groups)
              ? slice.groups
                  .map(group => (group && group.property ? group.property : ''))
                  .join(',')
              : ''
          }`}
        />
      );

    default:
      return null;
  }
};

export default Slice;

export const test = {
  formatBreadcrumbs,
};
